export const STOCK_UNIT_TYPES = [
  ['each', 'Each'],
  ['pair', 'Pair'],
  ['ml', 'ml'],
  ['litre', 'Litre'],
  ['metre', 'Metre'],
  ['pack', 'Pack'],
  ['kit', 'Kit'],
  ['custom', 'Custom'],
]

export const JOB_STOCK_USAGE_STATUSES = [
  ['draft', 'Draft'],
  ['confirmed_used', 'Confirmed used'],
  ['voided', 'Voided'],
]

const STATUS_TONES = {
  draft: 'muted',
  confirmed_used: 'green',
  voided: 'orange',
}

export function stockText(value) {
  return String(value || '').trim()
}

export function defaultStockUsageChargeable(sourceType = '', additionalWorkRequestId = null) {
  return !additionalWorkRequestId && stockText(sourceType).toLowerCase() !== 'general_additional_work'
}

export function stockNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

export function stockMoney(value) {
  return Math.round(Math.max(0, stockNumber(value)) * 100) / 100
}

export function stockQuantity(value) {
  return Math.round(Math.max(0, stockNumber(value)) * 1000) / 1000
}

export function stockMoneyLabel(value) {
  return `£${stockMoney(value).toFixed(2)}`
}

export function stockUnitLabel(value) {
  const key = stockText(value).toLowerCase()
  return STOCK_UNIT_TYPES.find(([unit]) => unit === key)?.[1] || 'Each'
}

export function stockUsageStatusLabel(value) {
  const key = normaliseStockUsageStatus(value)
  return JOB_STOCK_USAGE_STATUSES.find(([status]) => status === key)?.[1] || 'Draft'
}

export function stockUsageStatusTone(value) {
  return STATUS_TONES[normaliseStockUsageStatus(value)] || 'muted'
}

export function normaliseStockUsageStatus(value) {
  const key = stockText(value).toLowerCase()
  return JOB_STOCK_USAGE_STATUSES.some(([status]) => status === key) ? key : 'draft'
}

export function stockQuantityLabel(value, unitType = 'each') {
  const quantity = stockQuantity(value)
  const rounded = Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
  return `${rounded} ${stockUnitLabel(unitType).toLowerCase()}`
}

export function stockItemTitle(item = {}) {
  return stockText(item.name) || stockText(item.stock_name_snapshot) || 'Stock item'
}

export function stockItemMeta(item = {}) {
  return [
    stockText(item.category || item.stock_category_snapshot),
    stockText(item.brand || item.manufacturer || item.stock_brand_snapshot),
    stockText(item.internal_code || item.stock_internal_code_snapshot),
    stockText(item.supplier_part_number || item.stock_supplier_part_number_snapshot),
    stockText(item.supplier || item.stock_supplier_snapshot),
  ].filter(Boolean).join(' · ')
}

export function stockItemSearchText(item = {}) {
  return [
    item.name,
    item.category,
    item.brand,
    item.manufacturer,
    item.supplier,
    item.internal_code,
    item.supplier_part_number,
    item.barcode_gtin,
    item.description,
    item.notes,
  ].filter(Boolean).join(' ').toLowerCase()
}

export function isLowStock(item = {}) {
  const quantity = stockQuantity(item.quantity_on_hand)
  const reorder = stockQuantity(item.reorder_level)
  return Boolean(item.active !== false && reorder > 0 && quantity <= reorder)
}

export function stockLineCost(row = {}) {
  return stockMoney(stockQuantity(row.quantity || 1) * stockMoney(row.unit_cost_snapshot ?? row.cost_price))
}

export function stockLineSell(row = {}) {
  if (Number.isSafeInteger(row.vat_price_snapshot?.tax?.gross_minor)) return row.vat_price_snapshot.tax.gross_minor / 100
  return stockMoney(stockQuantity(row.quantity || 1) * stockMoney(row.unit_sell_price_snapshot ?? row.sell_price))
}

export function stockUsagePayloadFromItem(item = {}, quantity = 1) {
  const qty = stockQuantity(quantity) || 1
  return {
    stock_item_id: item.id || null,
    quantity: qty,
    unit_type: item.unit_type || 'each',
    stock_name_snapshot: stockText(item.name),
    stock_category_snapshot: stockText(item.category),
    stock_brand_snapshot: stockText(item.brand || item.manufacturer),
    stock_supplier_snapshot: stockText(item.supplier),
    stock_internal_code_snapshot: stockText(item.internal_code),
    stock_supplier_part_number_snapshot: stockText(item.supplier_part_number),
    stock_barcode_gtin_snapshot: stockText(item.barcode_gtin),
    unit_cost_snapshot: stockMoney(item.cost_price),
    unit_sell_price_snapshot: stockMoney(item.sell_price),
    customer_description: stockText(item.description) || stockText(item.name),
  }
}

export function partRowFromStockItem(item = {}, quantity = 1) {
  const payload = stockUsagePayloadFromItem(item, quantity)
  return {
    id: `stock-${item.id || 'item'}-${Date.now()}`,
    source_type: 'stock_usage',
    stock_item_id: payload.stock_item_id,
    name: payload.stock_name_snapshot,
    part_number: payload.stock_internal_code_snapshot || payload.stock_supplier_part_number_snapshot,
    supplier: payload.stock_supplier_snapshot,
    quantity: String(payload.quantity),
    unit_cost: payload.unit_cost_snapshot ? String(payload.unit_cost_snapshot) : '',
    unit_price: payload.unit_sell_price_snapshot ? String(payload.unit_sell_price_snapshot) : '',
  }
}

export function serviceKitPartRowId(kit = {}) {
  const source = stockText(kit.id) || stockText(kit.part_number).toUpperCase() || 'manual'
  return `kit-${source}`
}

export function partRowFromServiceKit(kit = {}) {
  const price = Array.isArray(kit.kit_prices) ? kit.kit_prices[0] : null
  return {
    id: serviceKitPartRowId(kit),
    name: stockText(kit.description) || 'Service kit',
    part_number: stockText(kit.part_number),
    supplier: stockText(price?.supplier),
    quantity: '1',
    unit_cost: price?.unit_cost ? String(price.unit_cost) : '',
    unit_price: '',
  }
}

export function isServiceKitPartRow(row = {}) {
  return stockText(row.id).startsWith('kit-')
}

export function jobPartRowIdentity(row = {}) {
  const explicitId = stockText(row.id)
  if (explicitId) return explicitId

  return [
    stockText(row.part_number).toUpperCase(),
    stockText(row.name).toLowerCase(),
    stockText(row.supplier).toLowerCase(),
  ].join('|')
}

export function mergeJobPartRows(rows = [], nextRow = {}, options = {}) {
  const currentRows = Array.isArray(rows) ? rows : []
  if (!nextRow || typeof nextRow !== 'object') return currentRows

  const nextIdentity = jobPartRowIdentity(nextRow)
  if (options.replaceMatching && nextIdentity) {
    const existingIndex = currentRows.findIndex(row => jobPartRowIdentity(row) === nextIdentity)
    if (existingIndex >= 0) {
      return currentRows.map((row, index) => (index === existingIndex ? { ...row, ...nextRow } : row))
    }
  }

  return [...currentRows, nextRow]
}

export function partsMaterialFromStockItem(item = {}, quantity = 1) {
  const payload = stockUsagePayloadFromItem(item, quantity)
  const lineAmount = stockLineSell(payload)
  return {
    description: `${payload.quantity} x ${payload.stock_name_snapshot}`,
    quantity: payload.quantity,
    amount: lineAmount,
    stock_item_id: payload.stock_item_id,
  }
}

export function isMissingStockSchemaError(error) {
  const message = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].map(stockText).join(' ').toLowerCase()

  return message.includes('42p01')
    || message.includes('42703')
    || message.includes('42883')
    || message.includes('schema cache')
    || message.includes('could not find the table')
    || message.includes('could not find the function')
    || (message.includes('relation') && message.includes('does not exist'))
}
